import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { WeeklyRosterData, WeeklyPlayerCard } from '../../types';
import { v1Api } from '../../services/api';
import GradingLoader from '../common/GradingLoader';

interface WeeklyLineupPanelProps {
  teamId: number;
  week: number | null;
  teamName?: string;
  onClose: () => void;
}

const WeeklyLineupPanel: React.FC<WeeklyLineupPanelProps> = ({
  teamId,
  week,
  teamName,
  onClose
}) => {
  const [rosterData, setRosterData] = useState<WeeklyRosterData | null>(null);
  const [opponentRoster, setOpponentRoster] = useState<WeeklyRosterData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { espnLeagueId } = useParams<{ espnLeagueId: string }>();

  useEffect(() => {
    if (!week) return;

    const loadRosterData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await v1Api.teams.getWeeklyRoster(teamId, week);
        setRosterData(response);
        
        // Load opponent roster if there's a matchup
        if (response.matchup?.opponent_id) {
          try {
            const opponentResponse = await v1Api.teams.getWeeklyRoster(response.matchup.opponent_id, week);
            setOpponentRoster(opponentResponse);
          } catch (opponentErr) {
            console.error('Error loading opponent roster:', opponentErr);
            // Don't fail the whole component if opponent data fails
          }
        }
      } catch (err) {
        setError('Failed to load roster data');
        console.error('Error loading roster:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRosterData();
  }, [teamId, week]);

  if (!week) return null;

  const starters = rosterData?.roster?.filter(p => p.is_starter) || [];
  const bench = rosterData?.roster?.filter(p => !p.is_starter) || [];

  const PlayerCard: React.FC<{ player: WeeklyPlayerCard }> = ({ player }) => {
    const pointsDelta = player.week_points - player.projected_points;
    const isPositive = pointsDelta > 0;

    return (
      <div
        className="bg-background-secondary rounded-lg p-2.5 border border-fieldline cursor-pointer hover:bg-background-hover transition-colors"
        onClick={() => navigate(`/leagues/espn/${espnLeagueId}/players/${player.player_id}`)}
      >
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-background-tertiary text-text-secondary">
                {player.position}
              </span>
              <span className="text-xs text-text-tertiary">{player.nfl_team}</span>
            </div>
            <h4 className="font-medium text-text-primary truncate mt-0.5 text-sm">{player.name}</h4>
            {player.opponent_matchup && (
              <p className="text-xs text-text-secondary">{player.opponent_matchup}</p>
            )}
          </div>
          <div className="text-right ml-2">
            <div className="text-base font-bold text-text-primary">
              {player.week_points.toFixed(1)}
            </div>
            <div className="text-xs text-text-secondary">
              proj {player.projected_points.toFixed(1)}
            </div>
            {Math.abs(pointsDelta) > 0.1 && (
              <div className={`flex items-center text-xs ${
                isPositive ? 'text-accent-primary' : 'text-accent-danger'
              }`}>
                {isPositive ? (
                  <TrendingUp className="w-3 h-3 mr-1" />
                ) : (
                  <TrendingDown className="w-3 h-3 mr-1" />
                )}
                {Math.abs(pointsDelta).toFixed(1)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed inset-y-0 right-0 w-[800px] bg-background-primary border-l border-fieldline transform transition-transform duration-300 ease-in-out z-50 ${
      week ? 'translate-x-0' : 'translate-x-full'
    }`}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-fieldline">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-accent-info" />
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Week {week} Matchup</h2>
              {rosterData?.matchup && (
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-accent-info font-medium">{teamName || 'Your Team'}</span>
                  <span className="text-text-secondary">vs</span>
                  <span className="text-text-secondary">{rosterData.matchup.opponent_name}</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                    rosterData.matchup.result === 'win' 
                      ? 'bg-accent-primary/15 text-accent-primary' 
                      : 'bg-accent-danger/15 text-accent-danger'
                  }`}>
                    {rosterData.matchup.team_score.toFixed(1)} - {rosterData.matchup.opponent_score.toFixed(1)}
                  </span>
                </div>
              )}
              <div className="text-xs text-text-tertiary mt-1">
                Use ← → arrows to navigate weeks • ESC to close
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-background-hover rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <GradingLoader label="Grading the matchup…" size="sm" className="p-8" />
          )}

          {error && (
            <div className="p-4 text-center">
              <p className="text-accent-danger text-sm">{error}</p>
            </div>
          )}

          {rosterData && !loading && (
            <div className="grid grid-cols-2 h-full">
              {/* Your Team */}
              <div className="border-r border-fieldline p-4 space-y-4 overflow-y-auto">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-accent-info">
                    {teamName || 'Your Team'}
                  </h3>
                  <span className="text-xl font-bold text-text-primary">
                    {rosterData.matchup?.team_score.toFixed(1) || starters.reduce((sum, p) => sum + p.week_points, 0).toFixed(1)}
                  </span>
                </div>

                {/* Starting Lineup */}
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2 flex items-center">
                    Starting Lineup
                    <span className="ml-2 text-xs text-text-tertiary">({starters.length})</span>
                  </h4>
                  <div className="space-y-1.5">
                    {starters
                      .sort((a, b) => {
                        // Define position order for grouping
                        const positionOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'D/ST'];
                        const slotOrder = ['QB', 'RB', 'RB', 'WR', 'WR', 'TE', 'FLEX', 'K', 'D/ST'];
                        
                        // First sort by position group
                        const aPositionIndex = positionOrder.indexOf(a.position);
                        const bPositionIndex = positionOrder.indexOf(b.position);
                        
                        if (aPositionIndex !== bPositionIndex) {
                          return aPositionIndex - bPositionIndex;
                        }
                        
                        // Then by lineup slot within the same position
                        const aSlotIndex = slotOrder.indexOf(a.lineup_slot);
                        const bSlotIndex = slotOrder.indexOf(b.lineup_slot);
                        return aSlotIndex - bSlotIndex;
                      })
                      .map(player => (
                        <PlayerCard key={player.player_id} player={player} />
                      ))}
                  </div>
                </div>

                {/* Bench */}
                {bench.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2 flex items-center">
                      Bench
                      <span className="ml-2 text-xs text-text-tertiary">({bench.length})</span>
                    </h4>
                    <div className="space-y-1.5">
                      {bench
                        .sort((a, b) => b.week_points - a.week_points)
                        .slice(0, 6)
                        .map(player => (
                          <PlayerCard key={player.player_id} player={player} />
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Opponent Team */}
              <div className="p-4 space-y-4 overflow-y-auto">
                {opponentRoster ? (
                  <>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-text-secondary">
                        {rosterData.matchup?.opponent_name || 'Opponent'}
                      </h3>
                      <span className="text-xl font-bold text-text-primary">
                        {rosterData.matchup?.opponent_score.toFixed(1) || opponentRoster.roster.filter(p => p.is_starter).reduce((sum, p) => sum + p.week_points, 0).toFixed(1)}
                      </span>
                    </div>

                    {/* Opponent Starting Lineup */}
                    <div>
                      <h4 className="text-sm font-medium text-text-secondary mb-2 flex items-center">
                        Starting Lineup
                        <span className="ml-2 text-xs text-text-tertiary">({opponentRoster.roster.filter(p => p.is_starter).length})</span>
                      </h4>
                      <div className="space-y-1.5">
                        {opponentRoster.roster
                          .filter(p => p.is_starter)
                          .sort((a, b) => {
                            // Define position order for grouping
                            const positionOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'D/ST'];
                            const slotOrder = ['QB', 'RB', 'RB', 'WR', 'WR', 'TE', 'FLEX', 'K', 'D/ST'];
                            
                            // First sort by position group
                            const aPositionIndex = positionOrder.indexOf(a.position);
                            const bPositionIndex = positionOrder.indexOf(b.position);
                            
                            if (aPositionIndex !== bPositionIndex) {
                              return aPositionIndex - bPositionIndex;
                            }
                            
                            // Then by lineup slot within the same position
                            const aSlotIndex = slotOrder.indexOf(a.lineup_slot);
                            const bSlotIndex = slotOrder.indexOf(b.lineup_slot);
                            return aSlotIndex - bSlotIndex;
                          })
                          .map(player => (
                            <PlayerCard key={player.player_id} player={player} />
                          ))}
                      </div>
                    </div>

                    {/* Opponent Bench */}
                    <div>
                      <h4 className="text-sm font-medium text-text-secondary mb-2 flex items-center">
                        Bench
                        <span className="ml-2 text-xs text-text-tertiary">({opponentRoster.roster.filter(p => !p.is_starter).length})</span>
                      </h4>
                      <div className="space-y-1.5">
                        {opponentRoster.roster
                          .filter(p => !p.is_starter)
                          .sort((a, b) => b.week_points - a.week_points)
                          .slice(0, 6)
                          .map(player => (
                            <PlayerCard key={player.player_id} player={player} />
                          ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-text-secondary">
                      {rosterData.matchup ? 'Loading opponent roster...' : 'No opponent data available'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeeklyLineupPanel;